import {
	EMPLOYER_COST_OPTIONS,
	STANDARD_WAITING_PERIOD_VALUES
} from '@/features/Quote/components/settings/coverage/constants';
import type { CoverageValues } from '@/types/app';

/**
 * Configurable rules system for detecting and fixing unconventional coverage configurations
 *
 * This module provides a maintainable way to define rules for:
 * 1. Detecting unconventional/infeasible configurations
 * 2. Suggesting fixes that are more conventional and likely to get more quotes
 * 3. Warning users about potential coverage downgrades
 */

/**
 * Type of unconventional configuration detected
 */
export type UnconventionalConfigType =
	| 'excessive_waiting_days' // 90+ waiting days
	| 'unusual_waiting_period' // Non-standard waiting period (not 10, 20, 30, 65, 130, 260)
	| 'reverse_coverage_progression' // Coverage percentages rising
	| 'zero_first_half_only' // 0% coverage first year first half only
	| 'zero_first_year_both_halves' // 0% coverage both halves of first year
	| 'low_coverage' // Any coverage <70%
	| 'high_or_decimal_employer_costs'; // Employer costs >30% or with decimals

/**
 * Severity level for the unconventional configuration
 */
export type SeverityLevel = 'critical' | 'warning' | 'info';

/**
 * Represents an unconventional configuration detection and fix
 */
export interface UnconventionalConfigRule {
	/** Unique identifier for this rule */
	id: string;
	/** Type of unconventional configuration */
	type: UnconventionalConfigType;
	/** Severity of the issue */
	severity: SeverityLevel;
	/** Function to detect if this rule applies */
	detect: (values: CoverageValues) => boolean;
	/** Function to suggest a fix */
	suggestFix: (values: CoverageValues) => CoverageValues;
	/** Whether this fix results in worse coverage than original */
	isDowngrade: boolean;
	/** Translation key for the warning message */
	warningKey: string;
	/** Translation key for the fix description */
	fixDescriptionKey: string;
	/** Translation key for the downgrade warning (if applicable) */
	downgradeWarningKey?: string;
}

/**
 * Configurable rules for detecting and fixing unconventional configurations
 *
 * IMPORTANT: To add new rules, simply add a new entry to this array.
 * Each rule should specify:
 * - How to detect the issue
 * - How to fix it
 * - Whether it's a coverage downgrade
 * - Translation keys for messaging
 */
export const UNCONVENTIONAL_CONFIG_RULES: UnconventionalConfigRule[] = [
	{
		id: 'excessive_waiting_90',
		type: 'excessive_waiting_days',
		severity: 'warning',
		detect: (values: CoverageValues) => values.waiting_period === 90,
		suggestFix: (values: CoverageValues) => ({
			...values,
			waiting_period: 130
		}),
		isDowngrade: true,
		warningKey: 'quotes.coverageWarning.excessiveWaiting90',
		fixDescriptionKey: 'quotes.coverageWarning.fixExcessiveWaiting90',
		downgradeWarningKey: 'quotes.coverageWarning.downgradeWaitingPeriod'
	},
	{
		id: 'unusual_waiting',
		type: 'unusual_waiting_period',
		severity: 'warning',
		detect: (values: CoverageValues) => {
			// Don't trigger if excessive_waiting_days rule already handles it (>= 90)
			if (values.waiting_period >= 90) {
				return false;
			}
			return !STANDARD_WAITING_PERIOD_VALUES.includes(values.waiting_period);
		},
		suggestFix: (values: CoverageValues) => {
			const current = values.waiting_period;
			// Find the nearest standard value
			const nearest = STANDARD_WAITING_PERIOD_VALUES.reduce((prev, curr) =>
				Math.abs(curr - current) < Math.abs(prev - current) ? curr : prev
			);
			return {
				...values,
				waiting_period: nearest
			};
		},
		isDowngrade: false, // Could be up or down
		warningKey: 'quotes.coverageWarning.unusualWaitingPeriod',
		fixDescriptionKey: 'quotes.coverageWarning.fixUnusualWaitingPeriod'
	},
	{
		id: 'reverse_coverage',
		type: 'reverse_coverage_progression',
		severity: 'warning',
		detect: (values: CoverageValues) => {
			const { waiting_period } = values;
			const {
				coverage_percentage_first_year_first_half: firstHalf,
				coverage_percentage_first_year_second_half: secondHalf,
				coverage_percentage_second_year_first_half: thirdHalf,
				coverage_percentage_second_year_second_half: fourthHalf
			} = values;

			// Define all three comparisons
			// Detect when coverage increases (not just non-decreasing)
			const comparisons = [
				firstHalf < secondHalf, // Comparison 1: first year first half < first year second half
				secondHalf < thirdHalf, // Comparison 2: first year second half < second year first half
				thirdHalf < fourthHalf // Comparison 3: second year first half < second year second half
			];

			// Skip comparisons based on waiting_period
			if (waiting_period >= 260) {
				// Only check comparison 3 for increases
				return comparisons[2];
			}
			if (waiting_period >= 130) {
				// Skip comparison 1, check if comparisons 2 or 3 show increases
				return comparisons[1] || comparisons[2];
			}

			// Check if any comparison shows an increase (coverage rising)
			return comparisons.some((comparison) => comparison);
		},
		suggestFix: (values: CoverageValues) => {
			// Isn't used
			return values;
		},
		isDowngrade: true,
		warningKey: 'quotes.coverageWarning.reverseCoverage',
		fixDescriptionKey: 'quotes.coverageWarning.fixReverseCoverage',
		downgradeWarningKey: 'quotes.coverageWarning.downgradeSecondYear'
	},
	{
		id: 'low_coverage',
		type: 'low_coverage',
		severity: 'warning',
		detect: (values: CoverageValues) => {
			const coverages = [
				values.coverage_percentage_first_year_first_half,
				values.coverage_percentage_first_year_second_half,
				values.coverage_percentage_second_year_first_half,
				values.coverage_percentage_second_year_second_half
			];
			// Check if any coverage is below 70%
			return coverages.some((coverage) => coverage > 0 && coverage < 70);
		},
		suggestFix: (values: CoverageValues) => {
			const coverages = [
				values.coverage_percentage_first_year_first_half,
				values.coverage_percentage_first_year_second_half,
				values.coverage_percentage_second_year_first_half,
				values.coverage_percentage_second_year_second_half
			];
			// Find the minimum of all non-zero coverages
			const nonZeroCoverages = coverages.filter((c) => c > 0);
			const minCoverage = Math.min(...nonZeroCoverages);
			const suggestedCoverage = Math.max(70, minCoverage);

			return {
				...values,
				coverage_percentage_first_year_first_half:
					values.coverage_percentage_first_year_first_half > 0
						? Math.max(values.coverage_percentage_first_year_first_half, suggestedCoverage)
						: values.coverage_percentage_first_year_first_half,
				coverage_percentage_first_year_second_half:
					values.coverage_percentage_first_year_second_half > 0
						? Math.max(values.coverage_percentage_first_year_second_half, suggestedCoverage)
						: values.coverage_percentage_first_year_second_half,
				coverage_percentage_second_year_first_half:
					values.coverage_percentage_second_year_first_half > 0
						? Math.max(values.coverage_percentage_second_year_first_half, suggestedCoverage)
						: values.coverage_percentage_second_year_first_half,
				coverage_percentage_second_year_second_half:
					values.coverage_percentage_second_year_second_half > 0
						? Math.max(values.coverage_percentage_second_year_second_half, suggestedCoverage)
						: values.coverage_percentage_second_year_second_half
			};
		},
		isDowngrade: false, // Improving coverage, not downgrading
		warningKey: 'quotes.coverageWarning.lowCoverage',
		fixDescriptionKey: 'quotes.coverageWarning.fixLowCoverage'
	},
	{
		id: 'high_or_decimal_employer_costs',
		type: 'high_or_decimal_employer_costs',
		severity: 'warning',
		detect: (values: CoverageValues) => {
			// Check if employer costs are >30% or have decimals
			return values.employer_costs > 30 || values.employer_costs % 1 !== 0;
		},
		suggestFix: (values: CoverageValues) => {
			// If >30%, bring down to 30%
			if (values.employer_costs > 30) {
				return {
					...values,
					employer_costs: 30
				};
			}

			// If has decimals, find closest lower round number
			const closestLower = EMPLOYER_COST_OPTIONS.filter((v) => v <= values.employer_costs).sort(
				(a, b) => b - a
			)[0];

			return {
				...values,
				employer_costs: closestLower || 0
			};
		},
		isDowngrade: true, // Reducing employer costs is a downgrade
		warningKey: 'quotes.coverageWarning.highOrDecimalEmployerCosts',
		fixDescriptionKey: 'quotes.coverageWarning.fixHighOrDecimalEmployerCosts',
		downgradeWarningKey: 'quotes.coverageWarning.downgradeEmployerCosts'
	}
];

/**
 * Result of detecting unconventional configurations
 */
export interface UnconventionalConfigDetection {
	/** Whether any unconventional configurations were detected */
	hasIssues: boolean;
	/** Rules that were triggered */
	triggeredRules: UnconventionalConfigRule[];
	/** Whether any of the fixes would result in a downgrade */
	hasDowngrade: boolean;
	/** Suggested fixed configuration */
	suggestedConfig: CoverageValues | null;
}

/**
 * Detects unconventional configurations and suggests fixes
 *
 * @param values Current coverage values to check
 * @returns Detection result with triggered rules and suggested fix
 */
export function detectUnconventionalConfig(values: CoverageValues): UnconventionalConfigDetection {
	const triggeredRules: UnconventionalConfigRule[] = [];

	// Check each rule
	for (const rule of UNCONVENTIONAL_CONFIG_RULES) {
		if (rule.detect(values)) {
			triggeredRules.push(rule);
		}
	}

	// If no issues found, return early
	if (triggeredRules.length === 0) {
		return {
			hasIssues: false,
			triggeredRules: [],
			hasDowngrade: false,
			suggestedConfig: null
		};
	}

	// Apply all fixes sequentially (in order of severity)
	const sortedRules = [...triggeredRules].sort((a, b) => {
		const severityOrder = { critical: 0, warning: 1, info: 2 };
		return severityOrder[a.severity] - severityOrder[b.severity];
	});

	let suggestedConfig = { ...values };
	for (const rule of sortedRules) {
		suggestedConfig = rule.suggestFix(suggestedConfig);
	}

	// Check if any fix is a downgrade
	const hasDowngrade = triggeredRules.some((rule) => rule.isDowngrade);

	return {
		hasIssues: true,
		triggeredRules,
		hasDowngrade,
		suggestedConfig
	};
}

/**
 * Checks if the current configuration is unconventional
 *
 * @param values Coverage values to check
 * @returns True if unconventional, false otherwise
 */
export function isUnconventionalConfig(values: CoverageValues): boolean {
	return UNCONVENTIONAL_CONFIG_RULES.some((rule) => rule.detect(values));
}

/**
 * Gets a summary of what will change with the suggested fix
 *
 * @param current Current coverage values
 * @param suggested Suggested coverage values
 * @returns Array of change descriptions with translation keys
 */
export function getSuggestedChanges(
	current: CoverageValues,
	suggested: CoverageValues
): Array<{ field: keyof CoverageValues; from: number; to: number; translationKey: string }> {
	const changes: Array<{
		field: keyof CoverageValues;
		from: number;
		to: number;
		translationKey: string;
	}> = [];

	if (current.waiting_period !== suggested.waiting_period) {
		changes.push({
			field: 'waiting_period',
			from: current.waiting_period,
			to: suggested.waiting_period,
			translationKey: 'quotes.coverageWarning.changeWaitingPeriod'
		});
	}

	if (current.employer_costs !== suggested.employer_costs) {
		changes.push({
			field: 'employer_costs',
			from: current.employer_costs,
			to: suggested.employer_costs,
			translationKey: 'quotes.coverageWarning.changeEmployerCosts'
		});
	}

	if (
		current.coverage_percentage_first_year_first_half !==
			suggested.coverage_percentage_first_year_first_half ||
		current.coverage_percentage_first_year_second_half !==
			suggested.coverage_percentage_first_year_second_half
	) {
		changes.push({
			field: 'coverage_percentage_first_year_first_half', // Representative field
			from: current.coverage_percentage_first_year_first_half,
			to: suggested.coverage_percentage_first_year_first_half,
			translationKey: 'quotes.coverageWarning.changeFirstYearCoverage'
		});
	}

	if (
		current.coverage_percentage_second_year_first_half !==
			suggested.coverage_percentage_second_year_first_half ||
		current.coverage_percentage_second_year_second_half !==
			suggested.coverage_percentage_second_year_second_half
	) {
		changes.push({
			field: 'coverage_percentage_second_year_first_half', // Representative field
			from: current.coverage_percentage_second_year_first_half,
			to: suggested.coverage_percentage_second_year_first_half,
			translationKey: 'quotes.coverageWarning.changeSecondYearCoverage'
		});
	}

	return changes;
}
